'use client';

import { useEffect } from 'react';
import { loadPostHog } from '@/lib/analytics/posthog-loader';

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;

    if (!key || !host || key.startsWith('phc_REPLACE')) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[PostHog] NEXT_PUBLIC_POSTHOG_KEY o HOST no configurado. Tracking desactivado.');
      }
      return;
    }

    // Detección mobile sin matchMedia ruidoso: useragent + viewport.
    // Lighthouse mobile mostró posthog-recorder.js generando 437ms de long task
    // y 52 KB de bundle externo. Mantenemos recorder (founder usa deep-dive
    // replays) pero diferimos su carga: init posthog SIN recording, luego
    // activar recording 3s después (post-LCP, fuera de TBT window).
    const isMobileDevice =
      typeof window !== 'undefined' &&
      (window.matchMedia('(max-width: 768px)').matches ||
        /Mobi|Android|iPhone/i.test(window.navigator.userAgent));

    type PaPostHogClientFull = {
      __loaded?: boolean;
      init: (key: string, options: Record<string, unknown>) => void;
      startSessionRecording?: () => void;
      register?: (props: Record<string, unknown>) => void;
      unregister?: (key: string) => void;
    };

    const init = async () => {
      const posthog = (await loadPostHog()) as PaPostHogClientFull | null;
      if (!posthog || posthog.__loaded) return;

      posthog.init(key, {
        api_host: host,
        person_profiles: 'identified_only',
        capture_pageview: false,
        capture_pageleave: true,
        autocapture: true,
        // Surveys no se usan en este sitio. Lighthouse marcó surveys.js
        // como 80% no usado (~28 KB wasted) — desactivar evita la carga
        // del bundle externo y reduce TBT mobile.
        disable_surveys: true,
        // Init SIN session recording para evitar 52 KB de posthog-recorder.js
        // durante el TBT window. Se activa manualmente abajo con delay.
        disable_session_recording: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '[data-mask]',
        },
        loaded: (ph: { opt_out_capturing?: () => void }) => {
          if (process.env.NODE_ENV === 'development') ph.opt_out_capturing?.();
        },
      });

      // Tráfico interno (founder/QA/agentes) — auditoría 2026-06-10 §5: los
      // insights no distinguían pruebas de clientes reales. Visitar cualquier
      // URL con ?ph_internal=1 marca este navegador de forma persistente
      // (?ph_internal=0 lo desmarca) y todos sus eventos llevan la
      // super-property is_internal=true para excluirlos en PostHog.
      try {
        const flag = new URLSearchParams(window.location.search).get('ph_internal');
        if (flag === '1') localStorage.setItem('ph_internal', '1');
        if (flag === '0') localStorage.removeItem('ph_internal');
        if (localStorage.getItem('ph_internal') === '1') {
          posthog.register?.({ is_internal: true });
        } else {
          // Limpiar una marca previa: register persiste en el storage de
          // posthog y sin unregister quedaría pegada tras ?ph_internal=0.
          posthog.unregister?.('is_internal');
        }
      } catch {
        /* localStorage puede no estar disponible (modo privado estricto) */
      }

      // Activar recording POST-LCP (3s desktop, 5s mobile). Esto difiere la
      // carga del bundle externo posthog-recorder.js fuera del TBT crítico
      // sin perder replays — solo se omiten los primeros 3-5s de la sesión
      // que rara vez son interesantes para PostHog deep-dive.
      const recordingDelay = isMobileDevice ? 5000 : 3000;
      window.setTimeout(() => {
        try {
          posthog.startSessionRecording?.();
        } catch {
          /* recorder puede no estar disponible — best effort */
        }
      }, recordingDelay);
    };

    // La primera interacción real inicializa de inmediato. En una visita
    // pasiva mobile imponemos un piso temporal: requestIdleCallback por sí solo
    // dispara demasiado pronto y hacía que el SDK compitiera con hidratación,
    // video y hero. La cookie first-party conserva gclid/UTMs mientras llega el
    // $pageview diferido.
    let fired = false;
    let idleId: number | undefined;
    let timerId: number | undefined;
    const start = () => {
      if (fired) return;
      fired = true;
      cleanup();
      void init();
    };
    const onInteract = () => start();
    const cleanup = () => {
      window.removeEventListener('pointerdown', onInteract, true);
      window.removeEventListener('keydown', onInteract, true);
      window.removeEventListener('wheel', onInteract, true);
      if (idleId !== undefined && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timerId !== undefined) window.clearTimeout(timerId);
    };
    window.addEventListener('pointerdown', onInteract, { capture: true, passive: true, once: true });
    window.addEventListener('keydown', onInteract, { capture: true, once: true });
    window.addEventListener('wheel', onInteract, { capture: true, passive: true, once: true });
    if (isMobileDevice) {
      timerId = window.setTimeout(() => {
        timerId = undefined;
        if (typeof window.requestIdleCallback === 'function') {
          idleId = window.requestIdleCallback(start, { timeout: 2000 });
        } else {
          start();
        }
      }, 9000);
    } else if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(start, { timeout: 2000 });
    } else {
      timerId = window.setTimeout(start, 800);
    }
    return cleanup;
  }, []);

  return <>{children}</>;
}
